import { useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { sendSms } from "../../../lib/api/sendSms";
import { sendEmail } from "../../../lib/api/sendEmail";

await fetch("/functions/v1/run-reminders-daily?force=1&account_id=<ACCOUNT_UUID>", {
  headers: { Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}` },
});

export default function Tests() {
  // --- SMS state
  const [to, setTo] = useState("6945151249");
  const [text, setText] = useState("Δοκιμή SMS από Atmedia CRM");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // {type: "success"|"error", text: string}

  // --- Email state
  const [emailTo, setEmailTo] = useState("atsaknis@gmail.com");
  const [emailSubject, setEmailSubject] = useState("Δοκιμή Email από Atmedia CRM");
  const [emailHtml, setEmailHtml] = useState(
    "<p>Γεια! Αυτό είναι ένα <strong>δοκιμαστικό</strong> email.</p>"
  );
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null); // {type, text}

  const handleSendSms = async () => {
    setMsg(null);
    setLoading(true);
    try {
      const res = await sendSms(to, text);
      if (res?.ok) {
        setMsg({ type: "success", text: `OK → στάλθηκε σε ${res.to || to}` });
      } else {
        setMsg({ type: "error", text: `Aποτυχία: ${JSON.stringify(res)}` });
      }
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Aποτυχία αποστολής" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setEmailMsg(null);
    setEmailLoading(true);
    try {
      const res = await sendEmail({
        to: emailTo, // string ή {email,name} ή array
        subject: emailSubject,
        html: emailHtml, // μπορείς να βάλεις text αντί για html
        // cc: [], bcc: [], tags: ["test"], attachments: [...]
      });
      setEmailMsg({ type: "success", text: "OK → email στάλθηκε" });
      console.log("Brevo response:", res);
    } catch (e) {
      setEmailMsg({ type: "error", text: e?.message || "Aποτυχία αποστολής email" });
    } finally {
      setEmailLoading(false);
    }
  };

  const [accountId, setAccountId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey =
    process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  async function runReminders() {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const qs = new URLSearchParams({ force: "1" });
      if (accountId.trim()) qs.set("account_id", accountId.trim());

      const url = `${supabaseUrl}/functions/v1/run-reminders-daily?${qs.toString()}`;

      const r = await fetch(url, {
        method: "GET",
        headers: {
          // Τα functions θέλουν apikey & Authorization με το anon key στο browser
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
          "Content-Type": "application/json",
        },
      });

      const text = await r.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!r.ok || data?.ok === false) {
        throw new Error(data?.error || JSON.stringify(data));
      }

      setResult(data);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4}>
        <Grid container spacing={3}>
          {/* SMS TEST */}
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" mb={2}>
                  Test αποστολής SMS
                </MDTypography>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label="Κινητό (π.χ. 6945…)"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    inputProps={{ inputMode: "numeric" }}
                  />
                </MDBox>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Μήνυμα"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </MDBox>

                <MDBox display="flex" gap={2} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleSendSms}
                    disabled={loading || !to || !text}
                  >
                    {loading ? <CircularProgress size={22} /> : "Αποστολή δοκιμής"}
                  </Button>
                </MDBox>

                {msg && (
                  <MDBox mt={2}>
                    <Alert severity={msg.type}>{msg.text}</Alert>
                  </MDBox>
                )}

                <MDBox mt={3}>
                  <MDTypography variant="button" color="text">
                    Σημείωση: το format του αριθμού θα γίνει server-side σε 03069… όπως το ορίσαμε.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* EMAIL TEST */}
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" mb={2}>
                  Test αποστολής Email
                </MDTypography>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label="Παραλήπτης (email)"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                  />
                </MDBox>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label="Θέμα"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </MDBox>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="HTML περιεχόμενο"
                    value={emailHtml}
                    onChange={(e) => setEmailHtml(e.target.value)}
                  />
                </MDBox>

                <MDBox display="flex" gap={2} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleSendEmail}
                    disabled={emailLoading || !emailTo || !emailSubject || !emailHtml}
                  >
                    {emailLoading ? <CircularProgress size={22} /> : "Αποστολή δοκιμής Email"}
                  </Button>
                </MDBox>

                {emailMsg && (
                  <MDBox mt={2}>
                    <Alert severity={emailMsg.type}>{emailMsg.text}</Alert>
                  </MDBox>
                )}

                <MDBox mt={3}>
                  <MDTypography variant="button" color="text">
                    Σημείωση: το Brevo API key είναι κρυφό στη function. Μπορείς να χρησιμοποιήσεις
                    HTML ή απλό text.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDBox display="flex" gap={2} alignItems="center">
              <TextField
                label="Account ID (προαιρετικό)"
                size="small"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="c01f9... (ή άδειο για όλα)"
                sx={{ width: 420 }}
              />
              <Button variant="contained" onClick={runReminders} disabled={loading}>
                {loading ? "Τρέχει..." : "Τρέξ’ το τώρα (force)"}
              </Button>
            </MDBox>

            {error && (
              <MDBox color="error.main" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                {error}
              </MDBox>
            )}

            {result && (
              <MDBox
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: "#0f172a",
                  color: "#e2e8f0",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: 420,
                }}
              >
                {JSON.stringify(result, null, 2)}
              </MDBox>
            )}
          </MDBox>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
